import { useCallback, useRef } from "react";

/** Whether a run is in flight; one box per composer, held across renders. */
export type SingleFlightLatch = { current: boolean };

/**
 * Runs `action` unless a previous run is still in flight.
 *
 * A second press of a button therefore does nothing while the first press is still working. The latch is passed in
 * rather than held here because the guard has to outlive the callback it guards: a composer rebuilds its send on
 * every keystroke, and a latch inside would be a fresh one each time.
 *
 * @param latch - Set for the length of the run and cleared afterwards, whether the action resolves or rejects.
 * @param action - Awaited when the latch is clear; its rejection reaches the caller unchanged.
 * @param args - Passed to `action` untouched.
 */
export async function runSingleFlight<Args extends unknown[]>(
  latch: SingleFlightLatch,
  action: (...args: Args) => Promise<void>,
  ...args: Args
): Promise<void> {
  if (latch.current) return;
  latch.current = true;
  try {
    await action(...args);
  } finally {
    latch.current = false;
  }
}

/**
 * Wraps an async action so only one run of it is ever in flight.
 *
 * Presses that land while it is working are dropped rather than starting a run of their own. Disabling a button on
 * state does not do this on its own — the press that arrives before React commits the state reads the old value and
 * goes through. The latch here lives in a ref, so it is already set for that press.
 *
 * @param action - Rebuilt by the caller as often as it likes; the latch belongs to the component, not to the action.
 * @returns The action, guarded. Calls it dropped resolve without doing anything.
 */
export function useSingleFlight<Args extends unknown[]>(
  action: (...args: Args) => Promise<void>,
): (...args: Args) => Promise<void> {
  const latch = useRef(false);
  // `latch` is deliberately outside the deps: it is a ref, and the guard must survive `action` being rebuilt.
  return useCallback((...args: Args) => runSingleFlight(latch, action, ...args), [action]);
}
